import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useToast } from '@/shared/ui/Toast';
import { reviewSmsApi, type ProgramSettingsInput, type RequestMethod } from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';

type ApiErrorBody = {
  error?: string | { message?: string; code?: string; errors?: Record<string, string[]> };
  message?: string;
};

/** The backend refused a test because one on this phone is partway through (asks "Start over?"). */
export function isTestInProgress(err: unknown): boolean {
  const body = (err as { response?: { data?: ApiErrorBody } } | null)?.response?.data;
  return typeof body?.error === 'object' && body.error?.code === 'TEST_IN_PROGRESS';
}

/** "brand.website" -> "website", "brand.postalCode" -> "postal code". */
function fieldLabel(path: string): string {
  const last = path.split('.').pop() ?? path;
  return last.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
}

/**
 * The API's message: `{ error: { message } }` (the backend's shape), `{ error: '…' }` or
 * `{ message }`. A validation failure names the fields instead of a bare "Validation failed".
 */
function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: ApiErrorBody }; data?: ApiErrorBody; message?: string };
  const body = e.response?.data ?? e.data;
  const apiError = body?.error;
  const fields = typeof apiError === 'object' ? apiError?.errors : undefined;
  if (fields && Object.keys(fields).length > 0)
    return `Please check: ${Object.entries(fields)
      .map(([field, msgs]) => `${fieldLabel(field)} (${msgs.join(', ')})`)
      .join('; ')}`;
  return (
    (typeof apiError === 'string' ? apiError : apiError?.message) ??
    body?.message ??
    e.message ??
    'Something went wrong'
  );
}

const EDIT_ROLES = new Set(['owner', 'admin', 'manager']);

/** Owners and office managers edit settings; the front desk uploads, confirms and takes over (PRD 4.6). */
export function useCanEditProgram(): boolean {
  const { user } = useAuth();
  return !!user && (user.isAgency || EDIT_ROLES.has(user.role ?? ''));
}

const DELETE_ROLES = new Set(['owner', 'admin']);

/** Only owners and admins delete conversations: it erases a patient's thread from the practice's view. */
export function useCanDeleteConversations(): boolean {
  const { user } = useAuth();
  return !!user && (user.isAgency || DELETE_ROLES.has(user.role ?? ''));
}

/** Owners and admins enter or remove the practice's Oryx login (the same roles the backend allows). */
export function useCanConnectOryx(): boolean {
  const { user } = useAuth();
  return !!user && (user.isAgency || DELETE_ROLES.has(user.role ?? ''));
}

export function useLocationId(): string {
  const { currentLocation } = useLocation();
  return currentLocation?.id ?? '';
}

/** The program view plus every mutation that changes it; all invalidate the same key. */
export function useSmsProgram() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: reviewKeys.smsProgram(locationId) });

  const query = useQuery({
    queryKey: reviewKeys.smsProgram(locationId),
    queryFn: () => reviewSmsApi.getProgram(locationId),
    enabled: !!locationId,
  });

  const update = useMutation({
    mutationFn: (input: ProgramSettingsInput) => reviewSmsApi.updateProgram(locationId, input),
    onSuccess: () => {
      invalidate();
      toast.success('Saved');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const pause = useMutation({
    mutationFn: () => reviewSmsApi.pauseProgram(locationId),
    onSuccess: () => {
      invalidate();
      toast.success('Texting paused. Open conversations still answer STOP and HELP.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const resume = useMutation({
    mutationFn: () => reviewSmsApi.resumeProgram(locationId),
    onSuccess: () => {
      invalidate();
      toast.success('Texting resumed');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const goLive = useMutation({
    mutationFn: () => reviewSmsApi.goLive(locationId),
    onSuccess: (view) => {
      invalidate();
      toast.success(
        view.status === 'live'
          ? "Text requests are live. Today's confirmed visits are next."
          : 'Set up. Texts queue until carrier registration clears.',
      );
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const sendTest = useMutation({
    mutationFn: ({ phone, replace }: { phone: string; replace?: boolean }) =>
      reviewSmsApi.sendTest(locationId, phone, replace),
    onSuccess: (result) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: reviewKeys.smsDays(locationId) });
      toast.success(
        `Test on its way to ${result.appointment.mobileMasked}${result.program.test.usesSharedNumber ? " from PracticeNova's shared number" : ''}.`,
      );
    },
    // A test in progress is asked about on the page ("Start over?"), not shown as an error.
    onError: (err: unknown) => {
      if (!isTestInProgress(err)) toast.error(errorMessage(err));
    },
  });
  const disableTest = useMutation({
    mutationFn: () => reviewSmsApi.disableTestMode(locationId),
    onSuccess: () => {
      invalidate();
      toast.success('Test mode off');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const setMethod = useMutation({
    mutationFn: (method: RequestMethod) => reviewSmsApi.setRequestMethod(locationId, method),
    onSuccess: (view) => {
      invalidate();
      toast.success(
        view.requestMethod === 'link'
          ? 'Get reviews now uses your link and QR code. Texting is paused.'
          : 'Get reviews now texts patients after their visit.',
      );
    },
    onError: (err: unknown) => {
      invalidate();
      toast.error(errorMessage(err));
    },
  });

  return { ...query, locationId, update, pause, resume, goLive, sendTest, disableTest, setMethod };
}

export { errorMessage };
